/**
 * Mole-ratio stoichiometry on a balanced equation.
 *
 * Given the moles of one species, every other species follows from the coefficients:
 *
 *     n(X) = n(given) × coefficient(X) ÷ coefficient(given)
 *
 * and moles convert to grams by the molar mass and to litres of gas by the molar volume.
 */

import type { Species } from "@/lib/tool-kit/chem-reactions/balance";
import { molarMassOf } from "@/lib/tool-kit/chemistry/formula";

export interface StoichRow {
  index: number;
  species: Species;
  coefficient: number;
  /** g/mol; null for the electron. */
  molarMass: number | null;
  /** The molar mass is a mass number, not a standard atomic weight. */
  approximate: boolean;
  moles: number;
  grams: number | null;
  /** Litres of gas, for species typed with (g). */
  litres: number | null;
  isGiven: boolean;
}

export interface SpeciesMass {
  total: number | null;
  approximate: boolean;
}

/** Molar mass of one species; null for the electron. */
export function speciesMass(species: Species): SpeciesMass {
  if (species.electron) {
    return { total: null, approximate: false };
  }
  const mass = molarMassOf(species.counts);
  return { total: mass.total, approximate: mass.usesMassNumber };
}

/** A species is treated as a gas when it was typed with (g). */
export function isGas(species: Species): boolean {
  return species.state === "g";
}

export function stoichiometryRows(
  species: readonly Species[],
  coefficients: readonly number[],
  givenIndex: number,
  givenMoles: number,
  litresPerMole: number | null,
  /** Index of a species to treat as a gas even without (g): the one a volume was given for. */
  forcedGas: number | null = null
): StoichRow[] {
  const givenCoefficient = coefficients[givenIndex];
  return species.map((item, index) => {
    const moles = (givenMoles * coefficients[index]) / givenCoefficient;
    const { total, approximate } = speciesMass(item);
    return {
      index,
      species: item,
      coefficient: coefficients[index],
      molarMass: total,
      approximate,
      moles,
      grams: total === null ? null : moles * total,
      litres:
        (isGas(item) || index === forcedGas) && litresPerMole !== null
          ? moles * litresPerMole
          : null,
      isGiven: index === givenIndex,
    };
  });
}
